// =====================================================
// ONEFORM - EXPORTADOR DE FORMULÁRIOS PARA MARKDOWN
// =====================================================

import type { FormField, FormSettings, Form, FormResponse, FieldType } from '../../types/forms';
import { NON_INPUT_FIELD_TYPES } from '../../types/forms';
import { sanitizeFilenameTitle } from '../utils/filename';

/**
 * Exportar um formulário para formato Markdown
 */
export function exportFormToMarkdown(
  title: string,
  description: string,
  fields: FormField[],
  settings?: FormSettings
): string {
  const lines: string[] = [];

  // Frontmatter
  lines.push('---');
  lines.push(`title: ${title}`);
  if (description) {
    lines.push(`description: ${description}`);
  }

  // Settings
  if (settings && Object.keys(settings).length > 0) {
    lines.push('settings:');
    if (settings.showProgressBar !== undefined) {
      lines.push(`  showProgressBar: ${settings.showProgressBar}`);
    }
    if (settings.allowSave !== undefined) {
      lines.push(`  allowSave: ${settings.allowSave}`);
    }
    if (settings.notifyOnSubmit !== undefined) {
      lines.push(`  notifyOnSubmit: ${settings.notifyOnSubmit}`);
    }
    if (settings.notifyEmail) {
      lines.push(`  notifyEmail: ${settings.notifyEmail}`);
    }
    if (settings.submitButtonText) {
      lines.push(`  submitButtonText: ${settings.submitButtonText}`);
    }
    if (settings.nextButtonText) {
      lines.push(`  nextButtonText: ${settings.nextButtonText}`);
    }
    if (settings.showBranding !== undefined) {
      lines.push(`  showBranding: ${settings.showBranding}`);
    }
    if (settings.themeConfig) {
      lines.push(`  themeConfig: ${JSON.stringify(settings.themeConfig)}`);
    }
  }

  lines.push('---');
  lines.push('');

  // Agrupar campos por bloco/seção
  let currentBlock: string | undefined;

  for (const field of fields) {
    // Verificar se mudou de seção
    if (field.block && field.block !== currentBlock) {
      if (currentBlock !== undefined) {
        lines.push('---');
        lines.push('');
      }
      lines.push(`# Seção: ${field.block}`);
      lines.push('');
      currentBlock = field.block;
    } else if (!field.block && currentBlock !== undefined) {
      // Saiu de uma seção para campos sem seção
      lines.push('---');
      lines.push('');
      currentBlock = undefined;
    }

    // Label do campo
    const requiredMark = field.required ? ' *' : '';
    lines.push(`## ${field.label}${requiredMark}`);

    // Tipo
    lines.push(`- type: ${field.type}`);

    // Placeholder
    if (field.placeholder) {
      lines.push(`- placeholder: ${field.placeholder}`);
    }

    // Texto de ajuda
    if (field.helpText) {
      lines.push(`- help: ${field.helpText}`);
    }

    // Opções para campos de seleção
    if (field.options && field.options.length > 0) {
      lines.push(`- options: ${field.options.join(', ')}`);
    }

    // Validações
    if (field.validation) {
      if (field.validation.min !== undefined) {
        lines.push(`- min: ${field.validation.min}`);
      }
      if (field.validation.max !== undefined) {
        lines.push(`- max: ${field.validation.max}`);
      }
      if (field.validation.minLength !== undefined) {
        lines.push(`- minlength: ${field.validation.minLength}`);
      }
      if (field.validation.maxLength !== undefined) {
        lines.push(`- maxlength: ${field.validation.maxLength}`);
      }
      if (field.validation.fileTypes && field.validation.fileTypes.length > 0) {
        lines.push(`- accept: ${field.validation.fileTypes.join(', ')}`);
      }
    }

    // Largura
    if (field.width === 'half') {
      lines.push('- width: half');
    }

    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Exportar formulário completo (objeto Form) para Markdown
 */
export function exportFullFormToMarkdown(form: Form): string {
  return exportFormToMarkdown(
    form.title,
    form.description || '',
    form.fields,
    form.settings
  );
}

/**
 * Copiar Markdown para clipboard
 */
export async function copyMarkdownToClipboard(markdown: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(markdown);
    return true;
  } catch (err) {
    console.error('Erro ao copiar para clipboard:', err);
    return false;
  }
}

/**
 * Baixar Markdown como arquivo
 */
export function downloadMarkdownFile(markdown: string, filename: string): void {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.md') ? filename : `${filename}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// =====================================================
// Story saas-1.18 — Response Export (single + batch)
// Coexiste com o schema export acima (D11). Decisões D1-D11 ver story.
// =====================================================

const RESPONSE_STATUS_LABELS: Record<string, string> = {
  new: 'Novo',
  in_progress: 'Em andamento',
  completed: 'Concluído',
};

// Field types renderizados sem escape (sem chars MD por natureza).
const NO_ESCAPE_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'number',
  'opinion_scale',
  'nps',
  'rating',
  'yes_no',
  'date',
  'datetime',
  'time',
]);

// Escape full meta-chars MD (D6) para single-line texto/URLs/labels.
function escapeMarkdownChars(s: string): string {
  return s.replace(/([\\`*_{}[\]()#+!|>~-])/g, '\\$1');
}

// Wrap em code-fence (D6 multi-line). Usa fence dinâmico se conteúdo já contém ```
function wrapInCodeFence(s: string): string {
  let fence = '```';
  while (s.includes(fence)) fence += '`';
  return `${fence}\n${s}\n${fence}`;
}

// Escape mínimo de string YAML inline (D1 frontmatter).
// Quoting com double-quotes + escape de \ e " — cobre acentos, emojis, MD chars sem ambiguidade.
function escapeYamlInline(s: string): string {
  return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

// Render do valor de UM field (D2/D6/D7).
// Retorna string MD ja formatada (sem o heading `## Label`).
function formatFieldValueForMarkdown(field: FormField, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '_(sem resposta)_';
  }

  // Multi-value: array → lista. Cada item escapado per type.
  if (Array.isArray(value)) {
    const items = value
      .filter((v) => v !== null && v !== undefined && v !== '')
      .map((v) => formatScalarValueForMarkdown(field, v));
    if (items.length === 0) return '_(sem resposta)_';
    return items.map((it) => `- ${it}`).join('\n');
  }

  return formatScalarValueForMarkdown(field, value);
}

// Render de um único valor escalar respeitando D6.
function formatScalarValueForMarkdown(field: FormField, value: unknown): string {
  // Render direto (sem chars MD): numeric/boolean/date.
  if (NO_ESCAPE_FIELD_TYPES.has(field.type)) {
    return String(value);
  }

  // Coerção defensiva pra picture_choice single (pode vir como objeto).
  let str: string;
  if (typeof value === 'object') {
    try {
      str = JSON.stringify(value);
    } catch {
      str = String(value);
    }
  } else {
    str = String(value);
  }

  // Multi-line (D7): converte \n→\n\n e wrap em code-fence pra preservar literal.
  if (str.includes('\n')) {
    const normalized = str.replace(/\r\n/g, '\n').replace(/\n+/g, '\n\n');
    return wrapInCodeFence(normalized);
  }

  // textarea single-line ainda usa code-fence pra previsibilidade (D6 long_text).
  if (field.type === 'textarea') {
    return wrapInCodeFence(str);
  }

  // Single-line text/email/url/phone/select/radio: escape full meta-chars.
  return escapeMarkdownChars(str);
}

// Gera o frontmatter YAML (D1).
function buildFrontmatter(response: FormResponse, form: Form): string {
  const lines: string[] = ['---'];
  lines.push(`form_title: ${escapeYamlInline(form.title || '')}`);
  lines.push(`form_id: ${escapeYamlInline(form.id || response.form_id)}`);
  lines.push(`response_id: ${escapeYamlInline(response.id)}`);
  lines.push(`submitted_at: ${escapeYamlInline(response.created_at)}`);
  lines.push(
    `status: ${escapeYamlInline(RESPONSE_STATUS_LABELS[response.status] ?? response.status)}`
  );
  lines.push('---');
  return lines.join('\n');
}

/**
 * Gera string MD de uma response (frontmatter + body).
 * Non-input fields são filtrados (consistente com CSV saas-1.15).
 */
export function exportResponseToMarkdown(response: FormResponse, form: Form): string {
  const inputFields = (form.fields || []).filter(
    (f) => !NON_INPUT_FIELD_TYPES.includes(f.type)
  );

  const sections: string[] = [];
  sections.push(buildFrontmatter(response, form));
  sections.push('');

  for (const field of inputFields) {
    const label = (field.label || field.id).trim() || field.id;
    sections.push(`## ${escapeMarkdownChars(label)}`);
    sections.push('');
    sections.push(formatFieldValueForMarkdown(field, response.data?.[field.id]));
    sections.push('');
  }

  return sections.join('\n').replace(/\n{3,}$/, '\n');
}

/**
 * Concat-único (D3) de N responses em um único MD.
 * Separador: `---` + heading `# Resposta {i}: {id8}`.
 */
export function exportResponsesToMarkdown(responses: FormResponse[], form: Form): string {
  if (responses.length === 0) return '';

  const blocks: string[] = [];
  responses.forEach((response, idx) => {
    if (idx > 0) {
      blocks.push('');
      blocks.push('---');
      blocks.push('');
    }
    blocks.push(`# Resposta ${idx + 1}: ${response.id.slice(0, 8)}`);
    blocks.push('');
    blocks.push(exportResponseToMarkdown(response, form));
  });

  return blocks.join('\n');
}

/**
 * Wrapper: gera + dispara download de UMA response.
 * Filename (D8): `{slug(title)}_{id.slice(0,8)}.md`
 */
export function downloadResponseMarkdown(response: FormResponse, form: Form): void {
  const md = exportResponseToMarkdown(response, form);
  const filename = `${sanitizeFilenameTitle(form.title)}_${response.id.slice(0, 8)}.md`;
  downloadMarkdownFile(md, filename);
}

/**
 * Wrapper: gera + dispara download de N responses concatenadas.
 * Filename (D8): `{slug(title)}-{N}-respostas-{YYYY-MM-DD}.md`
 */
export function downloadResponsesMarkdown(responses: FormResponse[], form: Form): void {
  const md = exportResponsesToMarkdown(responses, form);
  const isoDate = new Date().toISOString().slice(0, 10);
  const filename = `${sanitizeFilenameTitle(form.title)}-${responses.length}-respostas-${isoDate}.md`;
  downloadMarkdownFile(md, filename);
}
